import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import Layout from './components/Layout/Layout';
import DriverLayout from './components/driver/DriverLayout';
import PrivateRoute from './components/PrivateRoute';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import ForgotPassword from './pages/auth/ForgotPassword';
import OrganizationDashboard from './pages/organization/OrganizationDashboard';
import AcceptInvitationPage from './pages/organization/AcceptInvitationPage';
import DashboardPage from './pages/dashboard/DashboardPage';
import VehiclesPage from './pages/vehicle/VehiclesPage';
import SuppliersPage from './pages/supplier/SuppliersPage';
import MaintenanceRecordsPage from './pages/maintenance/MaintenanceRecordsPage';
import MaintenanceRecordDetailPage from './pages/maintenance/MaintenanceRecordDetailPage';
import EditMaintenanceRecordPage from './pages/maintenance/EditMaintenanceRecordPage';
import QuickMileageEntryPage from './pages/mileage/QuickMileageEntryPage';
import { QuickFuelReceiptPage } from './pages/fuel/QuickFuelReceiptPage';
import { QuickMaintenanceEntryPage } from './pages/maintenance/QuickMaintenanceEntryPage';
import { DriverDashboard } from './pages/driver/DriverDashboard';
import PublicReservationPage from './pages/reservation/PublicReservationPage';
import ReservationManagementPage from './pages/reservation/ReservationManagementPage';

// Import des pages d'importation CSV des consommations de carburant
import FuelImportPage from './pages/fuel/import/FuelImportPage';
import FuelMappingPage from './pages/fuel/import/FuelMappingPage';
import FuelProcessPage from './pages/fuel/import/FuelProcessPage';
import FuelResultsPage from './pages/fuel/import/FuelResultsPage';
import FuelUnknownVehiclesPage from './pages/fuel/import/FuelUnknownVehiclesPage';
import FuelImportHistoryPage from './pages/fuel/import/FuelImportHistoryPage';
import { useEffect } from 'react';
import { useAuth } from './contexts/AuthContext';
import useUserRole from './hooks/useUserRole';

function App() {
  const { user, loading: authLoading } = useAuth();
  const { role, loading: roleLoading } = useUserRole();

  useEffect(() => {
    console.log('App render - Auth state:', { user, role });
  }, [user, role]);

  // Attendre que l'authentification et le rôle soient chargés
  if (authLoading || roleLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor="background.default"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Vérifier si l'utilisateur est un conducteur
  const isDriver = role === 'User';

  return (
    <Routes>
      {/* Routes publiques */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reserve/:publicLinkId" element={<PublicReservationPage />} />
      <Route path="/invitations" element={<PrivateRoute><AcceptInvitationPage /></PrivateRoute>} />

      {/* Routes protégées */}
      <Route path="/" element={<PrivateRoute>{isDriver ? <Navigate to="/driver" /> : <Navigate to="/admin" />}</PrivateRoute>} />

      {/* Routes conducteur */}
      <Route
        path="/driver"
        element={
          <PrivateRoute>
            <DriverLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<DriverDashboard />} />
        <Route path="mileage" element={<QuickMileageEntryPage />} />
        <Route path="fuel" element={<QuickFuelReceiptPage />} />
        <Route path="maintenance" element={<QuickMaintenanceEntryPage />} />
      </Route>

      {/* Routes admin */}
      <Route
        path="/admin"
        element={
          <PrivateRoute>
            <Layout />
          </PrivateRoute>
        }
      >
        <Route index element={<OrganizationDashboard />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="vehicles" element={<VehiclesPage />} />
        <Route path="suppliers" element={<SuppliersPage />} />
        <Route path="maintenance" element={<MaintenanceRecordsPage />} />
        <Route path="maintenance/:id" element={<MaintenanceRecordDetailPage />} />
        <Route path="maintenance/:id/edit" element={<EditMaintenanceRecordPage />} />
        <Route path="quick-mileage" element={<QuickMileageEntryPage />} />
        <Route path="quick-fuel" element={<QuickFuelReceiptPage />} />
        <Route path="quick-maintenance" element={<QuickMaintenanceEntryPage />} />
        <Route path="reservations" element={<ReservationManagementPage />} />
        
        {/* Routes d'importation CSV des consommations de carburant */}
        <Route path="fuel/import" element={<FuelImportPage />} />
        <Route path="fuel/import/mapping" element={<FuelMappingPage />} />
        <Route path="fuel/import/process" element={<FuelProcessPage />} />
        <Route path="fuel/import/results" element={<FuelResultsPage />} />
        <Route path="fuel/import/unknown-vehicles" element={<FuelUnknownVehiclesPage />} />
        <Route path="fuel/import/history" element={<FuelImportHistoryPage />} />
        <Route path="fuel/import/history/:id" element={<FuelImportHistoryPage />} />
      </Route>

      {/* Redirection par défaut */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
